// Der Netzwerk-Block aus dem Homelab-Protokoll (Topics `netz.cluster`,
// `openwrt.stand`, `link.stand`, `topologie.knoten`, `topologie.kanten`,
// `netz.lage`; docs/protokoll.md › „Netzwerk über das Protokoll“) in genau die
// Formen, die die Seiten aus den REST-Antworten kennen:
//   - GET /v2-api/network/topology → `NetworkTopology` je Cluster,
//   - GET /v2-api/openwrt/agents   → `OpenwrtSnapshot` je Router,
//   - GET /v2-api/link-state       → `LinkState` je Schnittstelle,
//   - GET /v2-api/topology         → `TopoNode` je Knoten, dazu Kanten und
//     Prüfkanten (`TopoGraph`),
//   - GET /v2-api/security/rekey-window → `RekeyWindow`,
//   - GET /v2-api/integrations     → `Integration`.
//
// Rein, ohne Runen und ohne Laufzeit-Importe, damit node (Test
// lib/netz-protokoll.test.mjs) sie laden kann. Die Stände kommen skaliert an
// (`skaliere("openwrt.stand", …)`: Mbit/s); Gegenstück der Rechnung ist der
// Adapter in internal/protokoll/adapter.
//
// Regel, mit der die REST-Antworten exakt wiederkehren (der Adapter sendet
// spiegelbildlich):
//   - Felder, die die REST-Antwort ohne `omitempty` führt, stehen immer: fehlt
//     der Wert im Stand, wird er ""/0/false bzw. `[]` (leere Listen fehlen im
//     Stand). Eine Ausnahme sind die Zeiger `online` der Rechner und Netzgeräte:
//     sie sind `null`, solange der Zustand unbekannt ist.
//   - Felder mit `omitempty` stehen am Ergebnis nur, wenn der Stand sie trägt
//     (Texte nicht leer, Zahlen ungleich 0, Teilobjekte `link`/`tailscale`
//     vorhanden, Listen nicht leer).
//   - Zeiten: `zeit_ms` (Unix-Millisekunden) werden ISO-Text (`last_seen`,
//     `expires_at`, `opened_at`); fehlt die Zeit, steht die Nullzeit von Go.
//     Die Zeiten der Topologie und der Verbindungen sind schon Texte (wie REST)
//     und gehen unverändert durch.
//   - Listen laufen in der Reihenfolge der REST-Antwort: Cluster und Knoten
//     tragen sie in `position`, Router und Verbindungen sind nach Kennung bzw.
//     `entity` sortiert; Kanten, Prüfkanten, Rekey-Fenster und Integrationen
//     kommen als Liste in dieser Reihenfolge.
//   - Die Instanz-Id: Cluster-Id bzw. Router-Kennung stehen direkt im
//     Instanznamen, bei Verbindungen und Knoten ist es ein Hash. Den Schlüssel
//     der Einträge tragen die Stände selbst (`id`, `router`, `entity`).

import type { NetworkHostEntry, NetworkDeviceEntry, NetworkTopology } from "./types";
import type { OpenwrtSnapshot, OpenwrtTailscale, OpenwrtTunnel } from "./network-details.svelte";
import type { LinkState, LinkStateSummary } from "./link-state.svelte";
import type { TopoCheckEdge, TopoEdge, TopoLinkInfo, TopoNode, TopoNodeCheck, TopoGraph } from "./topology-summary.svelte";
import type { RekeyWindow } from "./rekey-window.svelte";
import type { Integration } from "./integrations.svelte";
import type {
  Integration as IntegrationStand,
  LinkStand,
  NetzCluster,
  NetzGeraet,
  NetzHost,
  NetzLage,
  OpenwrtStand,
  OpenwrtTailscale as OpenwrtTailscaleStand,
  OpenwrtTunnel as OpenwrtTunnelStand,
  RekeyFenster,
  TopoKante,
  TopoLink,
  TopoPruefkante,
  TopoPruefung,
  TopologieKanten,
  TopologieKnoten,
} from "./protokoll/schema.gen";

/** So schreibt Go `time.Time{}` als JSON. */
export const NULLZEIT = "0001-01-01T00:00:00Z";

const HEX = /^[0-9a-f]+$/;

// Instanzname mit genau drei Segmenten `<familie>.<id>.<endung>` → id (sonst undefined).
function idAusInstanz(instanz: string, familie: string, endung: string, hex: boolean): string | undefined {
  const t = instanz.split(".");
  if (t.length !== 3 || t[0] !== familie || t[2] !== endung || t[1] === "") return undefined;
  if (hex && !HEX.test(t[1])) return undefined;
  return t[1];
}

/** „netz.wob.cluster“ → „wob“ (die Cluster-Id). `netz.lage` und alles andere → undefined. */
export function clusterIdAusInstanz(instanz: string): string | undefined {
  return idAusInstanz(instanz, "netz", "cluster", false);
}

/** „openwrt.openwrt.stand“ → „openwrt“ (die Kennung des Routers). Alles andere → undefined. */
export function routerIdAusInstanz(instanz: string): string | undefined {
  return idAusInstanz(instanz, "openwrt", "stand", false);
}

/** „link.ab12cd34ef56ab78.stand“ → „ab12cd34ef56ab78“ (Hash der entity). Alles andere → undefined. */
export function linkIdAusInstanz(instanz: string): string | undefined {
  return idAusInstanz(instanz, "link", "stand", true);
}

/** „topologie.ab12cd34ef56ab78.knoten“ → „ab12cd34ef56ab78“ (Hash der Knoten-Id).
 *  `topologie.kanten` und alles andere → undefined. */
export function knotenIdAusInstanz(instanz: string): string | undefined {
  return idAusInstanz(instanz, "topologie", "knoten", true);
}

// Wert, wenn vorhanden (nicht null/undefined), sonst Rückfall.
const s = (v: string | null | undefined): string => v ?? "";
const n = (v: number | null | undefined): number => v ?? 0;
const b = (v: boolean | null | undefined): boolean => v ?? false;
const z = <T>(v: T | null | undefined): T | null => v ?? null;

/** Setzt `o[k]`, wenn der Wert „da“ ist (omitempty: Text nicht leer, Zahl ungleich 0). */
function wenn<T extends object, K extends keyof T>(o: T, k: K, v: T[K] | null | undefined): void {
  if (v) o[k] = v as T[K];
}

/** Unix-Millisekunden → ISO-Text; fehlt/unlesbar → die Nullzeit der REST-Antwort. */
function zeit(ms: number | null | undefined): string {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return NULLZEIT;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? NULLZEIT : d.toISOString();
}

// ── Cluster ────────────────────────────────────────────────────────────

function host(h: NetzHost): NetworkHostEntry {
  const o: NetworkHostEntry = {
    id: s(h.id),
    name: s(h.name),
    ip: s(h.ip),
    mac: s(h.mac),
    role: s(h.rolle),
    sleep_ok: b(h.schlafen_ok),
    os: s(h.os),
    temp_threshold: n(h.temp_grenze),
    online: z(h.online),
  };
  if (h.noch_nicht_in_betrieb) o.noch_nicht_in_betrieb = true;
  return o;
}

function geraet(g: NetzGeraet): NetworkDeviceEntry {
  return {
    name: s(g.name),
    ip: s(g.ip),
    mac: s(g.mac),
    role: s(g.rolle),
    connected_to: s(g.verbunden_mit),
    online: z(g.online),
  };
}

/** Der Cluster aus seinem Stand, wie `GET /v2-api/network/topology` ihn führt.
 *  `id` fehlt im Stand nie; fehlt es doch, gilt die Id der Instanz. */
export function clusterAusProtokoll(c: NetzCluster, idInstanz = ""): NetworkTopology {
  return {
    id: c.id ?? idInstanz,
    name: s(c.name),
    subnet: s(c.subnetz),
    hosts: (c.hosts ?? []).map(host),
    devices: (c.geraete ?? []).map(geraet),
  };
}

/** Die Stelle des Clusters in der Liste des Collectors; fehlt sie → undefined. */
export function positionAusStand(p: { position?: number | null }): number | undefined {
  return typeof p.position === "number" && Number.isFinite(p.position) ? p.position : undefined;
}

/** Die Summe der Geräte über alle Cluster (devices_online, devices_total) wie
 *  die Cluster-Infra-Karte sie zeigt. */
export function geraeteSumme(topo: readonly NetworkTopology[]): { devices_online: number; devices_total: number } {
  let online = 0;
  let total = 0;
  for (const c of topo) {
    const devices = c.devices ?? [];
    total += devices.length;
    online += devices.filter((d) => d.online).length;
  }
  return { devices_online: online, devices_total: total };
}

// ── OpenWrt ────────────────────────────────────────────────────────────

function tunnel(t: OpenwrtTunnelStand): OpenwrtTunnel {
  const o: OpenwrtTunnel = { name: s(t.name), up: b(t.oben), uptime: n(t.laufzeit) };
  wenn(o, "ipv4", t.ipv4);
  wenn(o, "ipv6", t.ipv6);
  return o;
}

function tailscale(t: OpenwrtTailscaleStand): OpenwrtTailscale {
  const o: OpenwrtTailscale = { online: b(t.online), rx_mbps: n(t.empfang), tx_mbps: n(t.senden) };
  wenn(o, "ipv4", t.ipv4);
  wenn(o, "backend", t.backend);
  return o;
}

/** Der Router aus seinem Stand, wie `GET /v2-api/openwrt/agents` ihn führt.
 *  `router` fehlt im Stand nie; fehlt es doch, gilt die Kennung der Instanz. */
export function routerAusProtokoll(r: OpenwrtStand, idInstanz = ""): OpenwrtSnapshot {
  const o: OpenwrtSnapshot = {
    router: r.router ?? idInstanz,
    host: s(r.host),
    wan_iface: s(r.wan),
    rx_mbps: n(r.empfang),
    tx_mbps: n(r.senden),
    tunnels: (r.tunnel ?? []).map(tunnel),
    last_seen: zeit(r.zuletzt),
  };
  wenn(o, "topic", r.topic);
  if (r.tailscale != null) o.tailscale = tailscale(r.tailscale);
  return o;
}

// ── Verbindungen ───────────────────────────────────────────────────────

/** Die Schnittstelle aus ihrem Stand, wie `GET /v2-api/link-state` sie führt. */
export function linkAusProtokoll(l: LinkStand): LinkState {
  const o: LinkState = {
    entity: s(l.entity),
    host_id: s(l.host),
    iface: s(l.iface),
    state: s(l.zustand),
    health: s(l.gesundheit),
    since: s(l.seit),
    outage: b(l.ausfall),
    flaps: n(l.flaps),
  };
  wenn(o, "down_since", l.ausfall_seit);
  wenn(o, "last_flap", l.letzter_flap);
  wenn(o, "last_event", l.letztes_ereignis);
  return o;
}

/** Die Zusammenfassung der Verbindungen für Karten und Abzeichen. */
export function linkSumme(links: readonly LinkState[]): LinkStateSummary {
  const r: LinkStateSummary = { total: 0, up: 0, degraded: 0, outages: 0, flaps: 0 };
  for (const l of links) {
    r.total++;
    if (l.health === "up") r.up++;
    else if (l.health === "degraded") r.degraded++;
    if (l.outage) r.outages++;
    r.flaps += l.flaps || 0;
  }
  return r;
}

// ── Topologie ──────────────────────────────────────────────────────────

function verbindung(l: TopoLink): TopoLinkInfo {
  const o: TopoLinkInfo = { health: s(l.gesundheit), flaps: n(l.flaps), outage: b(l.ausfall) };
  wenn(o, "iface", l.iface);
  wenn(o, "last_flap", l.letzter_flap);
  return o;
}

function pruefung(p: TopoPruefung): TopoNodeCheck {
  const o: TopoNodeCheck = { vantage: s(p.blick), health: s(p.gesundheit) };
  wenn(o, "http_status", p.http_status);
  wenn(o, "latency_ms", p.latenz);
  wenn(o, "url", p.url);
  wenn(o, "note", p.notiz);
  wenn(o, "last_probe", p.zuletzt);
  return o;
}

/** Der Knoten aus seinem Stand, wie `GET /v2-api/topology` ihn in `nodes` führt. */
export function knotenAusProtokoll(k: TopologieKnoten): TopoNode {
  const o: TopoNode = { id: s(k.id), label: s(k.label), kind: s(k.art), status: s(k.status) };
  wenn(o, "parent", k.eltern);
  wenn(o, "cluster", k.cluster);
  wenn(o, "role", k.rolle);
  wenn(o, "ip", k.ip);
  wenn(o, "os", k.os);
  if (k.link != null) o.link = verbindung(k.link);
  if (k.pruefungen && k.pruefungen.length > 0) o.checks = k.pruefungen.map(pruefung);
  return o;
}

function kante(k: TopoKante): TopoEdge {
  const o: TopoEdge = { id: s(k.id), source: s(k.quelle), target: s(k.ziel), kind: s(k.art) };
  wenn(o, "label", k.label);
  return o;
}

function pruefkante(k: TopoPruefkante): TopoCheckEdge {
  const o: TopoCheckEdge = {
    id: s(k.id),
    source: s(k.quelle),
    target: s(k.ziel),
    vantage: s(k.blick),
    health: s(k.gesundheit),
  };
  wenn(o, "label", k.label);
  return o;
}

/** Kanten und Prüfkanten des Graphen (`edges`, `check_edges` von `GET /v2-api/topology`). */
export interface Kanten {
  edges: TopoEdge[];
  check_edges: TopoCheckEdge[];
}

export function kantenAusProtokoll(k: TopologieKanten): Kanten {
  return { edges: (k.kanten ?? []).map(kante), check_edges: (k.pruefkanten ?? []).map(pruefkante) };
}

/** Der Graph, wie die REST-Antwort ihn führt, aus den Knoten (in `position`)
 *  und den Kanten. Ohne Kanten (noch keine bekannt): leere Listen. */
export function graphAusProtokoll(nodes: TopoNode[], kanten: Kanten | undefined): TopoGraph {
  return { nodes, edges: kanten?.edges ?? [], check_edges: kanten?.check_edges ?? [] };
}

// ── Lage: Rekey-Fenster und Integrationen ──────────────────────────────

function fenster(f: RekeyFenster): RekeyWindow {
  return {
    host: s(f.host),
    expires_at: zeit(f.bis),
    opened_at: zeit(f.geoeffnet),
    opened_by: s(f.von),
    reason: s(f.grund),
  };
}

function integration(i: IntegrationStand): Integration {
  return {
    id: s(i.id),
    name: s(i.name),
    status: s(i.status),
    detail: s(i.detail),
    last_seen_unix: n(i.zuletzt),
    device_total: n(i.geraete),
    device_online: n(i.geraete_online),
    aggregate_status: s(i.gesamt),
  };
}

/** Die offenen Rekey-Fenster, wie `GET /v2-api/security/rekey-window` sie führt. */
export function rekeyAusProtokoll(l: NetzLage): RekeyWindow[] {
  return (l.rekey ?? []).map(fenster);
}

/** Die Integrationen, wie `GET /v2-api/integrations` sie führt. */
export function integrationenAusProtokoll(l: NetzLage): Integration[] {
  return (l.integrationen ?? []).map(integration);
}

// ── Reihenfolgen ───────────────────────────────────────────────────────

// Go vergleicht Texte Byte für Byte, hier Codeeinheit für Codeeinheit -- gleich,
// solange kein Name Zeichen außerhalb der Grundebene trägt.
function vergleicheText(a: string, b2: string): number {
  if (a === b2) return 0;
  return a < b2 ? -1 : 1;
}

/** Die Reihenfolge von `GET /v2-api/openwrt/agents` (nach Kennung). */
export function vergleicheRouter(a: Pick<OpenwrtSnapshot, "router">, b2: Pick<OpenwrtSnapshot, "router">): number {
  return vergleicheText(a.router ?? "", b2.router ?? "");
}

/** Die Reihenfolge von `GET /v2-api/link-state` (nach entity). */
export function vergleicheLinks(a: Pick<LinkState, "entity">, b2: Pick<LinkState, "entity">): number {
  return vergleicheText(a.entity, b2.entity);
}

/** Die Reihenfolge der Quelle (Cluster, Knoten): nach Position, bei Gleichstand
 *  (oder ohne Position) nach Schlüssel. `position` liefert die Stelle zu einem
 *  Schlüssel (oder undefined = unbekannt, kommt ans Ende). */
export function vergleicheNachPosition<T>(
  schluessel: (e: T) => string,
  position: (k: string) => number | undefined,
): (a: T, b2: T) => number {
  return (a, b2) => {
    const ka = schluessel(a);
    const kb = schluessel(b2);
    const pa = position(ka) ?? Number.POSITIVE_INFINITY;
    const pb = position(kb) ?? Number.POSITIVE_INFINITY;
    if (pa !== pb) return pa < pb ? -1 : 1;
    return vergleicheText(ka, kb);
  };
}
